import { useShellContext } from '@omni/kit';
import HealthMonitor from '@omni/kit/HealthMonitor';
import { KitLoader, KitText } from '@omni/kit/components';
import KitModalV2 from '@omni/kit/components/KitModalV2';
import KitNotFound from '@omni/kit/components/KitNotFound';
import { useScreenContext } from '@omni/kit/contexts/ScreenContext';
import {
  SizeClass,
  SizeClassV2,
  WindowSizeMin,
  useSizeClass,
} from '@omni/kit/contexts/SizeClassContext';
import GroupsService from '@omni/kit/services/GroupsService';
import { IGroupEvent } from '@omni/kit/services/GroupsService/Types';
import Colors from '@omni/kit/theming/Colors';
import { SpacingType } from '@omni/kit/theming/SpacingType';
import { colorForScheme } from '@omni/kit/theming/Theming';
import { excludedStatusCodes } from '@omni/kit/utilities/excludedStatusCodes';
import { useIsFocused } from '@react-navigation/native';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { useSelector } from 'react-redux';

import { useChannelGroup } from '../../../hooks/useChannelGroup';
import { useTransformGroupEvents } from '../../../hooks/useTransformGroupEvents';
import { channelSelector } from '../../redux/selectors';
import CreateEditGroupEventScreen from './CreateEditGroupEventScreen';
import GroupAllListEvents from './components/GroupAllListEvents';

interface Props {
  navigation: any;
  route: any;
}
export default function GroupEventListScreen({
  route,
  navigation,
}: Props): JSX.Element {
  const { tokens, user, app } = useShellContext();
  const appKey = app.appKey;
  const endUserId = user?.id;
  const accessToken = useMemo(() => tokens.user || null, [tokens.user]);
  const { smallestWindowWidth, windowWidth } = useSizeClass();
  const { sizeClass } = useSizeClass();
  const userToken = accessToken?.replace('Bearer ', '');
  const channel = useSelector(channelSelector) as GroupChannel;
  let focused: boolean;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    focused = useIsFocused();
  } catch {
    focused = true; // Default to focused for web environments without navigation context
  }
  const group = useChannelGroup(channel?.url);
  const isResponseError = route.params.responseError;
  const [groupEventList, setGroupEventList] = useState<any[] | undefined>();
  const [isLoadingGroupEventList, setIsLoadingGroupEventList] =
    useState<boolean>(true);
  const [refresh, setRefresh] = useState(false);

  // Displaying current and future events
  const upcomingEvents = useMemo(() => {
    const now = new Date();

    return groupEventList
      ?.filter((event) => {
        const startTime = new Date(event.start_at);
        const endTime = new Date(event.end_at);

        return (startTime <= now && endTime >= now) || startTime > now;
      })
      .sort(
        (a, b) =>
          new Date(a.start_at || 0).getTime() -
          new Date(b.start_at || 0).getTime()
      );
  }, [groupEventList]);

  const transformedGroupEvents = useTransformGroupEvents(
    upcomingEvents ? upcomingEvents : []
  );

  useEffect(() => {
    if (focused) {
      fetchGroupEventList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused]);

  const fetchGroupEventList = useCallback(() => {
    const _fetchGroupEventList = async () => {
      try {
        setIsLoadingGroupEventList(true);
        const response = await GroupsService.GetGroupEventList({
          token: accessToken,
          filters: [
            ['app_key', appKey],
            ['calendar.id', group?._embedded?.calendar?.id ?? ''],
            ['status', 'published'],
          ],
          pages: [['size', 100]],
        });

        if (response.body) {
          setGroupEventList(response.body._embedded?.events);
        } else if (
          response.status &&
          !excludedStatusCodes.includes(response.status)
        ) {
          HealthMonitor.logError(response.message);
        }
      } catch (error) {
        HealthMonitor.logError(error);
      } finally {
        setIsLoadingGroupEventList(false);
      }
    };

    if (group?._embedded?.calendar?.id) {
      _fetchGroupEventList();
    }
  }, [accessToken, appKey, group?._embedded?.calendar?.id]);

  // Hooks
  useEffect(() => {
    fetchGroupEventList();
  }, [fetchGroupEventList, refresh]);

  const [openEditGroupEventModal, setIsOpenEditGroupEventModal] =
    useState<boolean>(false);

  const openCreateEvent = () => {
    handleOnCreateGroupEvent();
  };
  const isSmall = sizeClass === SizeClass.Small;

  const handleOnCreateGroupEvent = () => {
    if (Platform.OS === 'web') {
      setIsOpenEditGroupEventModal(true);
    } else {
      navigation.navigate('create_event');
    }
  };
  const onRefreshSectionList = () => {
    fetchGroupEventList();
  };

  useEffect(() => {
    navigation.setParams({ openCreateEvent });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation]);

  return (
    <>
      {isLoadingGroupEventList ? (
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'white',
          }}
        >
          <KitLoader />
        </View>
      ) : isResponseError ? (
        <KitNotFound />
      ) : (
        <ScrollView
          style={[
            styles.container,
            {
              backgroundColor: colorForScheme({
                default: Colors.N0,
              }),
            },
          ]}
        >
          <GroupAllListEvents
            sections={transformedGroupEvents}
            onRefresh={onRefreshSectionList}
            isRefreshing={isLoadingGroupEventList}
            accessToken={accessToken}
            userToken={userToken}
            onPressCreateGroupEvent={handleOnCreateGroupEvent}
          />
        </ScrollView>
      )}
      <KitModalV2
        modalStyle={{
          width: '100%',
          maxWidth: 620,
          height: '100%',
          maxHeight: 700,
          padding: 0,
        }}
        coverScreen={true}
        anchorBottom={isSmall}
        isVisible={openEditGroupEventModal}
        onClose={() => setIsOpenEditGroupEventModal(false)}
        enableScroll
        noScrollView
      >
        <CreateEditGroupEventScreen
          onCancel={() => setIsOpenEditGroupEventModal(false)}
          onSuccess={() => setRefresh(true)}
        />
      </KitModalV2>
    </>
  );
}
const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    alignSelf: 'center',
  },
});
